<script lang="ts" setup>
import { onMounted, ref, computed, shallowRef, watch, nextTick } from 'vue';
import { unrefElement } from '@vueuse/core';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { useI18n } from '@/app/modules/i18n';
import { IconClose, IconChevronLeft, IconChevronRight } from '@/icons';
import {
  useZIndex,
  UiHeader,
  UiButton,
  UiButtonIcon,
  UiFloatingButtonIcon,
  ObFlexGrid,
  ObFlexGridItem,
  useTrueSelfClick,
} from '@/ui-kit';
import { MarkdownContent } from '../../../../components';
import { useAnnouncementsStore } from '../../stores';
import { Announcement } from '../../interfaces';

const announcementsStore = useAnnouncementsStore();
const { t } = useI18n();

const announcements = ref<Announcement[]>([]);

const index = ref(0);

const announcement = computed(() => {
  return announcements.value[index.value];
});

const active = ref(false);

function setCurrentViewed() {
  announcementsStore.setAnnouncementViewed(announcement.value.id);
}

function skipAll() {
  announcementsStore.setAllAnnouncementsViewed();
  active.value = false;
}

function onActionClick() {
  setCurrentViewed();
  active.value = false;
}

onMounted(() => {
  announcementsStore.fetch();
});

watch(
  () => announcementsStore.announcements,
  (value) => {
    // Initially filter out only not viewed announcements
    const viewedSet = new Set(announcementsStore.viewedAnnouncementsIds);
    announcements.value = value.filter(({ id }) => !viewedSet.has(id));
    active.value = announcements.value.length > 0;
  },
  { immediate: true },
);

const rootRef = shallowRef<HTMLElement | undefined>();
const innerRef = shallowRef<HTMLElement | undefined>();

const { zIndex } = useZIndex({ active: true, size: 2 });

const { activate: activateTrap, deactivate: deactivateTrap } = useFocusTrap(rootRef, {
  escapeDeactivates: false,
  allowOutsideClick: true,
  clickOutsideDeactivates: false,
  returnFocusOnDeactivate: true,
  fallbackFocus: () => unrefElement(rootRef) ?? 'body',
});

watch(
  active,
  (value, oldValue) => {
    if (value) {
      nextTick(() => {
        const rootEl = unrefElement(rootRef);

        if (!active.value) {
          return;
        }

        activateTrap();
        if (rootEl) {
          disableBodyScroll(rootEl, {
            reserveScrollBarGap: true,
          });
        }
      });
      return;
    }

    if (typeof oldValue === 'undefined') {
      return;
    }

    const rootEl = unrefElement(rootRef);
    deactivateTrap();
    if (rootEl) {
      enableBodyScroll(rootEl);
    }
  },
  { immediate: true },
);

function onEscapePress() {
  setCurrentViewed();
  active.value = false;
}

function onCloseButtonClick() {
  setCurrentViewed();
  active.value = false;
}

useTrueSelfClick(innerRef, () => {
  active.value = false;
});

function next() {
  setCurrentViewed();
  index.value++;
}

function prev() {
  setCurrentViewed();
  index.value--;
}
</script>

<template>
  <Teleport v-if="active" to="body">
    <div
      v-if="announcement"
      ref="rootRef"
      :class="$style.root"
      role="dialog"
      aria-modal="true"
      :style="{ zIndex: zIndex && zIndex + 1 }"
      tabindex="-1"
      @keydown.esc.stop.capture="onEscapePress"
    >
      <div ref="innerRef" :class="$style.inner">
        <div :class="$style.container">
          <div :class="$style.closeButton" @click="onCloseButtonClick">
            <UiButtonIcon variant="tertiary">
              <IconClose />
            </UiButtonIcon>
          </div>
          <div v-if="announcements.length > 1" :class="$style.prevButton">
            <UiFloatingButtonIcon variant="secondary" size="l" :disabled="index <= 0" @click="prev()">
              <IconChevronLeft />
            </UiFloatingButtonIcon>
          </div>
          <div v-if="announcements.length > 1" :class="$style.nextButton">
            <UiFloatingButtonIcon
              variant="secondary"
              size="l"
              :disabled="index >= announcements.length - 1"
              @click="next()"
            >
              <IconChevronRight />
            </UiFloatingButtonIcon>
          </div>
          <div :class="$style.dots">
            <button
              v-for="(item, i) in announcements"
              :key="i"
              type="button"
              tabindex="-1"
              :class="[$style.dot, { [$style.dotActive]: i === index }]"
              @click="index = i"
            />
          </div>
          <div :class="$style.box">
            <div :class="$style.cover">
              <div v-if="announcement.video" :class="$style.coverVideo">
                <video controls preload="metadata" playsinline autoplay muted>
                  <source :src="announcement.video" type="video/mp4" />
                </video>
              </div>
              <div v-else-if="announcement.image" :class="$style.coverImage">
                <img :src="announcement.image" alt="" />
              </div>
            </div>
            <div :class="$style.content">
              <UiHeader level="3">{{ announcement.title }}</UiHeader>
              <MarkdownContent v-if="announcement.excerpt" :class="$style.text" :content="announcement.excerpt" />
              <div v-if="announcements.length > 1 || announcement.actionLink">
                <ObFlexGrid>
                  <ObFlexGridItem v-if="announcements.length > 1">
                    <UiButton variant="tertiary" @click="skipAll()">
                      {{ announcements.length > 1 ? t('messages.skipAll') : t('messages.skip') }}
                    </UiButton>
                  </ObFlexGridItem>
                  <ObFlexGridItem v-if="announcement.actionLink" class="v:text-end">
                    <RouterLink v-slot="{ href, navigate }" :to="announcement.actionLink" custom>
                      <UiButton
                        variant="secondary"
                        as="a"
                        :href
                        @click="
                          navigate($event);
                          onActionClick();
                        "
                      >
                        {{ announcement.actionLabel }}
                      </UiButton>
                    </RouterLink>
                  </ObFlexGridItem>
                </ObFlexGrid>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" module>
.root {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(#000, 0.3);
  overflow: auto;
  outline: none;
  scrollbar-gutter: stable;
}

.inner {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  min-height: 100%;
  box-sizing: border-box;
}

.container {
  position: relative;
  width: 640px;
}

.closeButton {
  position: absolute;
  right: 8px;
  top: 8px;
  z-index: 100;
}

.prevButton,
.nextButton {
  position: absolute;
  z-index: 100;
  top: 50%;
  transform: translateY(-50%);
}

.prevButton {
  left: -48px;
}

.nextButton {
  right: -48px;
}

.box {
  background-color: #fff;
  border-radius: 10px;
  overflow: hidden;
}

.cover {
  position: relative;
  background: rgba(35, 57, 93, 0.1);
}

.coverVideo {
  position: relative;
  aspect-ratio: 16/10;
  overflow: hidden;

  video {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.coverImage {
  position: relative;
  aspect-ratio: 16/10;
  overflow: hidden;

  img {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
}

.text {
  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }

  > p {
    margin: 8px 0;
  }

  > ul,
  > ol {
    margin: 8px 0;
    padding: 0 0 0 24px;
  }
}

.dots {
  position: absolute;
  left: 50%;
  bottom: -20px;
  transform: translateX(-50%);
  display: flex;
  gap: 12px;
}

.dot {
  position: relative;
  appearance: none;
  box-sizing: border-box;
  border: none;
  outline: none;
  background: transparent;
  user-select: none;
  overflow: visible;
  font-family: inherit;
  padding: 0;
  text-decoration: none;
  touch-action: manipulation; // Avoid 300ms click delay on touch devices
  margin: 0; // Safari
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  width: 6px;
  height: 6px;
  border-radius: 6px;
  background: rgba(245, 245, 245, 0.75);

  &::before {
    content: '';
    display: block;
    position: absolute;
    left: -6px;
    right: -6px;
    top: -6px;
    bottom: -6px;
    border-radius: 50%;
  }
}

.dotActive {
  background: #7e57c2;
}
</style>
